import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import {
  Button,
  styled,
  Switch,
  useTheme,
} from "@monolith-forensics/monolith-ui";
import { EditIcon, GripVerticalIcon, TrashIcon } from "lucide-react";

import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";

import { calculateNewSortValue } from "./utils/calcNewValue";
import { capitalize } from "./utils/capitalize";
import { usePermissions } from "../../../hooks/usePermissions";

import Loader from "../../../components/Loader";
import CreateCustomField from "./components/CreateCustomField";
import EditCustomField from "./components/EditCustomField";
import DeleteCustomField from "./components/DeleteCustomField";

import type { CustomField as CustomFieldData } from "@/Features";
import type { CustomFieldAPIBundle } from "./utils/customFieldAPIBundler";

const StyledCustomFieldRow = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
  cursor: pointer;
  background-color: ${({ theme }) => theme.palette.background.default};
  &:hover {
    background-color: ${({ theme }) => theme.palette.action.hover};
  }
`;

const CreateFieldButtonWrapper = styled.div`
  margin: 25px 0px;
`;

const DragHandle = styled(GripVerticalIcon)`
  margin-right: 10px;
  cursor: move;
`;

const FieldInfoContainer = styled.div``;

const FieldNameInput = styled.div`
  font-size: larger;
  padding: 2px;
`;

const FieldMeta = styled.div`
  color: ${({ theme }) => theme.palette.text.secondary};
  padding: 2px;
`;

const FieldDescription = styled.div`
  color: ${({ theme }) => theme.palette.text.secondary};
  padding: 2px;
  white-space: nowrap;
  overflow-x: clip;
  max-width: 400px;
  text-overflow: ellipsis;
`;

const FieldId = styled.div`
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: 10px;
  padding: 2px;
`;

const ActionsContainer = styled.div`
  margin-left: auto;
`;

const ActionsRow = styled.div`
  font-size: 10px;
  color: ${({ theme }) => theme.palette.text.secondary};
  margin: 3px 5px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
`;

const EditorTypes: Record<string, string> = {
  textBox: "Text Box",
  dropDown: "Drop Down Menu",
  dateBox: "Date",
  tagBox: "Tag Box",
};

interface CustomFieldsTemplateProps {
  category: CustomFieldAPIBundle["id"];
  handleCreateField: CustomFieldAPIBundle["createField"];
  handleUpdateField: CustomFieldAPIBundle["updateField"];
  handleDeleteField: CustomFieldAPIBundle["deleteField"];
  getFields: CustomFieldAPIBundle["getFields"];
  getTypes: CustomFieldAPIBundle["getTypes"];
  description: string;
}

const CustomFieldsTemplate = ({
  category,
  handleCreateField,
  handleUpdateField,
  handleDeleteField,
  getFields,
  getTypes,
  description,
}: CustomFieldsTemplateProps) => {
  const [customFields, setCustomFields] = useState<CustomFieldData[] | null>(
    null
  );
  const [deleteSelection, setDeleteSelection] =
    useState<CustomFieldData | null>(null);
  const [editSelection, setEditSelection] = useState<CustomFieldData | null>(
    null
  );

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { data } = useQuery<CustomFieldData[]>({
    queryKey: ["custom-fields", `${category}`],
    queryFn: () => getFields({ category: category.toLowerCase() }),
  });

  useEffect(() => {
    if (data) {
      const fields = data
        .map((r) => ({
          ...r,
          options: r.options ?? [],
        }))
        .sort((a, b) => a.sort_value - b.sort_value);

      setCustomFields(fields);
    }
  }, [data]);

  const handleDelete = () => {
    if (!deleteSelection || !customFields) return;
    setShowDeleteModal(false);
    handleDeleteField(deleteSelection.field_id);
    setCustomFields(
      customFields.filter(
        (field) => field.field_id !== deleteSelection.field_id
      )
    );
  };

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;

    if (!destination) {
      return;
    }

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    if (!customFields) return;

    const newFields = Array.from(customFields);

    const [removed] = newFields.splice(source.index, 1);
    newFields.splice(destination.index, 0, removed);

    const newSortValue = calculateNewSortValue(newFields, destination);
    if (newSortValue === undefined) {
      setCustomFields(newFields);
      return;
    }
    newFields[destination.index].sort_value = newSortValue;
    setCustomFields(newFields);
    handleUpdateField({
      field_id: newFields[destination.index].field_id,
      sort_value: newSortValue,
    });
  };

  return (
    <>
      <DragDropContext onDragEnd={onDragEnd}>
        {!customFields && <Loader />}
        {customFields && (
          <>
            <div>{description}</div>
            <CreateFieldButtonWrapper>
              <Button
                variant="contained"
                color="primary"
                size="xs"
                onClick={() => setShowCreateModal(true)}
              >
                + Create Field
              </Button>
            </CreateFieldButtonWrapper>
          </>
        )}
        <Droppable droppableId="droppable-1">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {customFields &&
                customFields.map((field, index) => {
                  return (
                    <CustomField
                      key={field.field_id}
                      data={field}
                      index={index}
                      customFields={customFields}
                      setCustomFields={setCustomFields}
                      setDeleteSelection={setDeleteSelection}
                      setShowDeleteModal={setShowDeleteModal}
                      setEditSelection={setEditSelection}
                      setShowEditModal={setShowEditModal}
                      handleUpdateField={handleUpdateField}
                    />
                  );
                })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
        {customFields && (
          <>
            <CreateCustomField
              handleOpen={showCreateModal}
              handleClose={() => setShowCreateModal(false)}
              category={category}
              title={`Create Custom ${capitalize(category)} Field`}
              customFields={customFields}
              setCustomFields={setCustomFields}
              getTypes={getTypes}
              handleCreateField={handleCreateField}
            />
            <EditCustomField
              handleOpen={showEditModal}
              category={category}
              title={`Edit ${capitalize(category)} Field "${
                editSelection?.field_name || ""
              }"`}
              customFields={customFields}
              setCustomFields={setCustomFields}
              getTypes={getTypes}
              handleUpdateField={handleUpdateField}
              editSelection={editSelection}
              handleClose={() => setShowEditModal(false)}
            />
            <DeleteCustomField
              handleOpen={showDeleteModal}
              handleClose={() => setShowDeleteModal(false)}
              title={`Delete ${capitalize(category)} Field "${
                deleteSelection?.field_name || ""
              }"`}
              handleSubmit={handleDelete}
            />
          </>
        )}
      </DragDropContext>
    </>
  );
};

interface CustomFieldRowProps {
  data: CustomFieldData;
  index: number;
  customFields: CustomFieldData[];
  setCustomFields: React.Dispatch<
    React.SetStateAction<CustomFieldData[] | null>
  >;
  setDeleteSelection: React.Dispatch<
    React.SetStateAction<CustomFieldData | null>
  >;
  setEditSelection: React.Dispatch<
    React.SetStateAction<CustomFieldData | null>
  >;
  setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowDeleteModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleUpdateField: CustomFieldAPIBundle["updateField"];
}

const CustomField = ({
  data,
  index,
  customFields,
  setCustomFields,
  setDeleteSelection,
  setEditSelection,
  setShowEditModal,
  setShowDeleteModal,
  handleUpdateField,
}: CustomFieldRowProps) => {
  const theme = useTheme();
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();

  const handleUpdate = (event: React.FocusEvent<HTMLDivElement>) => {
    if (!hasPermission()) return;
    if (event.currentTarget.innerText === "") {
      event.currentTarget.innerText = data.field_name;
      return;
    }
    if (event.currentTarget.innerText !== data.field_name) {
      handleUpdateField({
        field_id: data.field_id,
        field_name: event.currentTarget.innerText,
      });
      setCustomFields(
        customFields.map((type) =>
          type.field_id === data.field_id
            ? { ...type, field_name: event.currentTarget.innerText }
            : type
        )
      );
    }
  };

  const handleDelete = () => {
    setDeleteSelection(data);
    setShowDeleteModal(true);
  };

  const handleEdit = () => {
    if (data.category === "inquiry") {
      queryClient.invalidateQueries({ queryKey: ["custom-fields", "case"] });
    }
    setEditSelection(data);
    setShowEditModal(true);
  };

  const handleEnableUpdate = (e: CustomFieldData) => {
    handleUpdateField({
      field_id: e.field_id,
      enabled: e.enabled,
    });
  };

  return (
    <Draggable draggableId={String(data.sort_value)} index={index}>
      {(provided) => (
        <StyledCustomFieldRow
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          ref={provided.innerRef}
        >
          <DragHandle size={16} color={theme.palette.text.secondary} />
          <FieldInfoContainer>
            <FieldNameInput
              contentEditable={hasPermission()}
              suppressContentEditableWarning
              onBlur={handleUpdate}
            >
              {data.field_name}
            </FieldNameInput>
            <FieldMeta>
              Required: {data.is_required === 1 ? "Yes" : "No"} | Editor:{" "}
              {EditorTypes[data.editor_type]}
            </FieldMeta>
            <FieldDescription title={data.description}>
              {data.description || ""}
            </FieldDescription>
            <FieldId>Field ID: {data.field_id}</FieldId>
          </FieldInfoContainer>
          <ActionsContainer>
            <ActionsRow>
              Enabled
              <Switch
                size="xs"
                defaultValue={data.enabled === 1}
                onChange={(value: boolean) => {
                  handleEnableUpdate({
                    ...data,
                    enabled: value ? 1 : 0,
                  });
                }}
              />
              <Button
                variant="subtle"
                color="primary"
                size="xs"
                disabled={!hasPermission()}
                onClick={handleEdit}
                leftSection={
                  <EditIcon size={14} color={theme.palette.primary.main} />
                }
              >
                Edit
              </Button>
              <Button
                variant="subtle"
                color="error"
                size="xs"
                disabled={!hasPermission()}
                onClick={handleDelete}
                leftSection={
                  <TrashIcon size={14} color={theme.palette.error.main} />
                }
              >
                Delete
              </Button>
            </ActionsRow>
          </ActionsContainer>
        </StyledCustomFieldRow>
      )}
    </Draggable>
  );
};

export default CustomFieldsTemplate;
